import { EmptyValue, FieldGrid } from "@/components/layout-2/ds";
import { Disclosure, InlineAlert, ListViewReference } from "@/components/layout-2/ds-settings";
import { DynamicFormField } from "@/components/layout-2/dynamic-form-field";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { buildDefaultValues } from "@/utils/dynamic-form";
import { ChevronLeft, Plus, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { recordsLabel } from "./counts";
import { configBody, configChanges, listViewChanges, NO_DRAFT, type NewView } from "./draft";
import { ListViewPicker, type ListViewOption } from "./list-view-picker";
import { StepDialog, type StepContext } from "./step-dialog";
import type { FeedingPageProps } from "./types";

const NO_OPTIONS = {};

/** What every step dialog gets from the overview. */
export type StepProps = Pick<FeedingPageProps, "page" | "send"> & { context: StepContext; onClose: () => void; onDone: () => void };

const newView = (view: ListViewOption): NewView => ({ key: `${view.id}-${Date.now()}`, name: view.name, object: view.object, records: view.records, spec: { mode: "duplicate", source_list_view_id: view.id } });

/* ------------------------------------------------------------------ */
/* Pace and rules: settings of the served form                          */
/* ------------------------------------------------------------------ */

const PACE_FIELDS = ["period_minutes", "cap_per_pass", "order"];
const RULE_FIELDS = ["allow_same_contact_multiple", "once_per_contact", "reinject_finished", "reinject_min_delay_days"];

type Values = Record<string, unknown>;

function useSettings(page: StepProps["page"], names: string[]) {
    const fields = useMemo(() => page.config.fields.filter(field => names.includes(field.field_name)), [page.config.fields]); // eslint-disable-line react-hooks/exhaustive-deps
    const defaults = useMemo(() => buildDefaultValues(fields, page.config.values) as Values, [fields, page.config.values]);
    return { fields, defaults };
}

/** Step 3: how often a pass runs, how many cards it creates at most, and which records come first. */
export function PaceStep({ page, send, context, onClose, onDone }: StepProps) {
    const { fields, defaults } = useSettings(page, PACE_FIELDS);
    const form = useForm<Values>({ defaultValues: defaults });
    const [capError, setCapError] = useState<string | null>(null);
    const values = form.watch();
    const changes = configChanges(fields, defaults, values);
    const cap = Number(values.cap_per_pass);
    useEffect(() => setCapError(null), [values.cap_per_pass]);

    return (
        <StepDialog
            title="Pace"
            description="How often the campaign looks for new records, and how many cards a pass creates at most."
            context={context}
            page={page}
            send={send}
            onClose={onClose}
            onDone={onDone}
            dirty={changes.length > 0 || (!page.config.exists && !!context.setup)}
            prepare={() => {
                if (!Number.isInteger(cap) || cap < 1) {
                    setCapError("The cap per pass is a whole number, 1 or more.");
                    return null;
                }
                return { body: { config: configBody(page, { period_minutes: Number(values.period_minutes), cap_per_pass: cap, order: values.order }) }, changes };
            }}
            testId="step-pace"
        >
            <Form {...form}>
                <form onSubmit={event => event.preventDefault()} className="flex flex-col gap-4">
                    {fields.map(field => (
                        <DynamicFormField key={field.field_name} field={field} form={form} dynamicOptions={NO_OPTIONS} loadingOptions={NO_OPTIONS} contextName="feeding" />
                    ))}
                    {capError && (
                        <p className="text-xs text-red-700" data-testid="cap-error">
                            {capError}
                        </p>
                    )}
                </form>
            </Form>
            {page.state !== "draft" && <InlineAlert type="info">Applies from the next pass.</InlineAlert>}
        </StepDialog>
    );
}

/** Step 4: the two questions that decide whether a record gets a card, each with an example. */
export function RulesStep({ page, send, context, onClose, onDone }: StepProps) {
    const { fields } = useSettings(page, RULE_FIELDS);
    // The values as saved, not as the served form would show them: the rules are not that form.
    const saved = page.config.values;
    const defaults = useMemo(() => Object.fromEntries(RULE_FIELDS.map(name => [name, saved[name as keyof typeof saved]])) as Values, [saved]);
    const [values, setValues] = useState<Values>(defaults);
    const [delayError, setDelayError] = useState<string | null>(null);
    const set = (patch: Values) => setValues(previous => ({ ...previous, ...patch }));
    const several = values.allow_same_contact_multiple === true;
    const reinject = values.reinject_finished === true;
    const delay = values.reinject_min_delay_days;
    const changes = configChanges(fields, defaults, values);

    const prepare = () => {
        const days = Number(delay);
        if (reinject && (delay === null || delay === "" || !Number.isInteger(days) || days < 0)) {
            setDelayError("Set the number of days, 0 or more.");
            return null;
        }
        return {
            body: {
                config: configBody(page, {
                    allow_same_contact_multiple: several,
                    once_per_contact: !several && values.once_per_contact === true,
                    reinject_finished: reinject,
                    reinject_min_delay_days: reinject ? days : null
                })
            },
            changes
        };
    };

    return (
        <StepDialog
            title="Rules"
            description="Two questions decide which records get a card. A record that already has a card in this campaign is always skipped."
            context={context}
            page={page}
            send={send}
            onClose={onClose}
            onDone={onDone}
            dirty={changes.length > 0}
            prepare={prepare}
            testId="step-rules"
        >
            <fieldset className="flex flex-col gap-3">
                <legend className="mb-3 text-sm font-semibold text-black">A contact has several matching records</legend>
                <RadioGroup value={several ? "record" : "contact"} onValueChange={value => set({ allow_same_contact_multiple: value === "record" })} className="gap-3">
                    <Choice value="contact" title="One card per contact" text="Default. The other records of the contact wait until that card is finished." />
                    {!several && (
                        <div className="ml-6 flex flex-col gap-2">
                            <label className="flex items-start gap-2 text-sm">
                                <Checkbox checked={values.once_per_contact === true} onCheckedChange={value => set({ once_per_contact: value === true })} aria-label="Never call a contact twice" data-testid="rule-once" />
                                <span>Never call a contact twice in this campaign, even after the card is finished</span>
                            </label>
                            <Example>A contact has 2 matching records: 1 card is created. With the box ticked, the second record never gets one, even once the first card is finished.</Example>
                        </div>
                    )}
                    <Choice value="record" title="One card per record" text="The contact can be called once for each of its records." />
                </RadioGroup>
                <Disclosure title="How is “the same contact” decided?">
                    <FieldGrid columns={1} items={page.help.dedup_keys.map(row => ({ label: row.object.label, value: row.key }))} />
                </Disclosure>
            </fieldset>

            <div className="h-px bg-gray-200" />

            <fieldset className="flex flex-col gap-3">
                <legend className="mb-3 text-sm font-semibold text-black">A card is finished but its record still matches</legend>
                <RadioGroup value={reinject ? "again" : "leave"} onValueChange={value => set({ reinject_finished: value === "again", reinject_min_delay_days: value === "again" ? (delay ?? 30) : delay })} className="gap-3">
                    <Choice value="leave" title="Leave it finished" text="Default. The record is not called again by this campaign." />
                    <Choice value="again" title="Give it a new card after a delay" text="The record comes back once the delay has passed since its card finished." />
                </RadioGroup>
                {reinject && (
                    <div className="ml-6 flex flex-col gap-2">
                        <div className="flex items-center gap-2 text-sm">
                            <Label htmlFor="reinject-delay">After</Label>
                            <Input
                                id="reinject-delay"
                                type="number"
                                min={0}
                                className="w-20"
                                value={delay === null || delay === undefined ? "" : String(delay)}
                                onChange={event => {
                                    setDelayError(null);
                                    set({ reinject_min_delay_days: event.target.value === "" ? null : Number(event.target.value) });
                                }}
                                data-testid="rule-delay"
                            />
                            <span>days</span>
                        </div>
                        {delayError && <p className="text-xs text-red-700">{delayError}</p>}
                        <Example>The record still matches {delay === null || delay === undefined || delay === "" ? "N" : String(delay)} days after its card finished: it gets a new card at the next pass.</Example>
                    </div>
                )}
            </fieldset>
            {page.state !== "draft" && <InlineAlert type="info">Rules apply from the next pass. Cards already in the campaign are not touched.</InlineAlert>}
        </StepDialog>
    );
}

function Choice({ value, title, text }: { value: string; title: string; text: string }) {
    return (
        <label className="flex cursor-pointer items-start gap-2 text-sm">
            <RadioGroupItem value={value} className="mt-0.5" aria-label={title} />
            <span className="flex flex-col">
                <span className="font-medium text-black">{title}</span>
                <span className="text-gray-600">{text}</span>
            </span>
        </label>
    );
}

function Example({ children }: { children: React.ReactNode }) {
    return <p className="rounded-sm border bg-gray-50 px-3 py-2 text-xs text-gray-600">{children}</p>;
}

/* ------------------------------------------------------------------ */
/* Exclusions                                                           */
/* ------------------------------------------------------------------ */

/** Step 5: the ListViews whose records are never called. Adding one picks it in the same dialog. */
export function ExclusionsStep({ page, send, context, onClose, onDone }: StepProps) {
    const { exclusions, source } = page;
    const [added, setAdded] = useState<NewView[]>([]);
    const [removed, setRemoved] = useState<number[]>([]);
    const [adding, setAdding] = useState(false);
    const [objectType, setObjectType] = useState(source.object_type);
    const [picked, setPicked] = useState<ListViewOption | null>(null);
    const limit = page.limits.max_source_records;
    // What an exclusion on the chosen object reaches, as the server explains it ("Account ListView": "Excludes…").
    const objectLabel = source.objects.find(item => item.type === objectType)?.label;
    const reach = page.help.exclusion_reach.find(row => row.label.toLowerCase().startsWith(`${objectLabel?.toLowerCase()} `))?.value;
    const draft = { ...NO_DRAFT, added, removed };
    const changes = listViewChanges(page, draft, [], "");

    const prepare = () => {
        // The first write creates the settings. An exclusion body alone does not, and attach then refuses it.
        const config = page.config.exists ? null : configBody(page, {});
        return {
            body: { ...(config ? { config } : {}), ...(added.length ? { exclusions_add: added.map(view => view.spec) } : {}), ...(removed.length ? { exclusions_remove: removed } : {}) },
            changes
        };
    };
    const toggle = (linkId: number) => setRemoved(previous => (previous.includes(linkId) ? previous.filter(id => id !== linkId) : [...previous, linkId]));

    const pickerFooter = (
        <>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)} data-testid="exclusion-back">
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <Button
                type="button"
                disabled={!picked}
                onClick={() => {
                    if (!picked) return;
                    setAdded(previous => [...previous, newView(picked)]);
                    setPicked(null);
                    setAdding(false);
                }}
                data-testid="exclusion-confirm"
            >
                Add to the exclusions
            </Button>
        </>
    );

    return (
        <StepDialog
            title={adding ? "Add an exclusion" : "Exclusions"}
            description={adding ? "Pick a standard ListView. The campaign gets its own copy: later changes to the original do not reach it." : "Records in these ListViews are never called by this campaign, and leave it if they are inside."}
            size="lg"
            context={context}
            page={page}
            send={send}
            onClose={onClose}
            onDone={onDone}
            dirty={changes.length > 0}
            prepare={prepare}
            footer={adding ? pickerFooter : undefined}
            testId="step-exclusions"
        >
            {adding ? (
                <ListViewPicker
                    campaignCode={page.campaign.code}
                    objects={source.objects}
                    objectType={objectType}
                    onObjectChange={setObjectType}
                    objectHint={reach}
                    onChange={setPicked}
                />
            ) : (
                <>
                    <div className="flex flex-col gap-2" data-testid="exclusion-list">
                        {exclusions.length === 0 && added.length === 0 && <p className="text-sm text-gray-600" data-testid="no-exclusions">No exclusion: every matching record can be called, including disputes and do-not-call lists.</p>}
                        {exclusions.map(view => {
                            const gone = removed.includes(view.link_id);
                            return (
                                <div key={view.link_id} data-testid={`exclusion-${view.id}`} className={gone ? "opacity-60" : undefined}>
                                    <ListViewReference
                                        name={view.name}
                                        object={view.object}
                                        badge={gone ? { label: "Removed on Save", tone: "warning" } : (view.status ?? undefined)}
                                        description={view.description}
                                        count={recordsLabel(view.matching_records, limit) ?? <EmptyValue />}
                                        actions={
                                            gone ? (
                                                <Button type="button" variant="ghost" size="sm" onClick={() => toggle(view.link_id)} data-testid={`restore-exclusion-${view.id}`}>
                                                    <Undo2 className="mr-1 h-4 w-4" /> Restore
                                                </Button>
                                            ) : undefined
                                        }
                                        onRemove={gone ? undefined : () => toggle(view.link_id)}
                                    />
                                </div>
                            );
                        })}
                        {added.map(view => (
                            <div key={view.key} data-testid={`new-exclusion-${view.key}`}>
                                <ListViewReference
                                    name={view.name}
                                    object={view.object}
                                    badge={{ label: "New", tone: "info" }}
                                    origin="Copy of a standard ListView, made on Save"
                                    count={recordsLabel(view.records, limit) ?? <EmptyValue />}
                                    onRemove={() => setAdded(previous => previous.filter(item => item.key !== view.key))}
                                />
                            </div>
                        ))}
                    </div>
                    <div>
                        <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="add-exclusion">
                            <Plus className="mr-1 h-4 w-4" /> Add a ListView
                        </Button>
                    </div>
                    <div data-testid="exclusion-scope">
                        <Disclosure title="How far does an exclusion reach?">
                            <FieldGrid columns={1} items={page.help.exclusion_reach} />
                        </Disclosure>
                    </div>
                </>
            )}
        </StepDialog>
    );
}
